import { Injectable, RendererFactory2, Renderer2, inject } from '@angular/core';
import {CdkDragEnter} from '@angular/cdk/drag-drop';
import {ReplaySubject, Observable, Subject} from 'rxjs';

import { TaskView, BaseTask } from '../../models';

@Injectable({
    providedIn: 'root'
})
export class DragDropService {
    private draggingDisabled$ = new Subject<boolean>();
    private readonly renderer: Renderer2 = null;
    private readonly draggingTask$: ReplaySubject<TaskView>;

    constructor() {
        const rendererFactory = inject(RendererFactory2);

        this.renderer = rendererFactory.createRenderer(null, null);
        this.draggingTask$ = new ReplaySubject(1);
    }

    public draggingTask(): Observable<TaskView> {
        return this.draggingTask$.asObservable();
    }

    public setDraggingDisabled(value: boolean) {
        this.draggingDisabled$.next(value);
    }
    public isDraggingDisabled(): Observable<boolean> {
        return this.draggingDisabled$.asObservable();
    }

    public setDraggingTask(task: TaskView): void {
        this.draggingTask$.next(task);
    }

    public hidePlaceholder(event: CdkDragEnter): void {
        const placeholderElement = event.item.getPlaceholderElement();

        if (placeholderElement.nextElementSibling) {
            this.renderer.appendChild(placeholderElement.nextElementSibling, placeholderElement);
        }

        this.renderer.addClass(placeholderElement, 'hide-placeholder');
        this.renderer.removeClass(placeholderElement, 'drag-placeholder-visible');
    }

    public showPlaceholder(event: CdkDragEnter): void {
        const placeholderElement = event.item.getPlaceholderElement();
        this.renderer.removeClass(placeholderElement, 'drag-placeholder-visible');
        this.renderer.removeClass(placeholderElement, 'hide-placeholder');
    }

    public disallowDragDrop(event: CdkDragEnter): void {
        this.renderer.addClass(event.container.element.nativeElement, 'drop-disallow');
    }

    public allowDragDrop(event: CdkDragEnter): void {
        this.renderer.removeClass(event.container.element.nativeElement, 'drop-disallow');
    }

    public resetPlaceholderPositionInCurrentContainer(event: CdkDragEnter<BaseTask[], BaseTask>): void {
        const placeholderElement = event.item.getPlaceholderElement();
        const rowElements = event.container.element.nativeElement.querySelectorAll('tr:not(.cdk-drag-placeholder)');
        const index = event.container.data.findIndex(i => i.originalTaskId === event.item.data.originalTaskId);

        if (index !== -1) {
            this.renderer.insertBefore(event.container.element.nativeElement, placeholderElement, rowElements[index]);
        }
    }

    public resetPlaceholderPositionInDropContainer(event: CdkDragEnter): void {
        const placeholderElement = event.item.getPlaceholderElement();
        const rowElements = event.item.dropContainer.element.nativeElement.querySelectorAll(
            'tr:not(.cdk-drag-placeholder)'
        );
        const index = event.item.dropContainer.data.findIndex(
            (i: BaseTask) => i.originalTaskId === event.item.data.originalTaskId
        );

        if (index !== -1) {
            this.renderer.insertBefore(
                event.item.dropContainer.element.nativeElement,
                placeholderElement,
                rowElements[index]
            );
        }
    }

    public resetPlaceholderPosition(event: CdkDragEnter<TaskView>): void {
        this.resetPlaceholderPositionInDropContainer(event);
        if (event.item.data.numberOfEmployees) {
            this.hidePlaceholder(event);
        } else {
            this.showPlaceholder(event);
        }
    }
}
